import * as React from "react";
import Box from "@mui/material/Box";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowModel,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import { IAppUserWebSystem } from "src/interface/IAppUserWebSystem";
import { ISystem } from "src/interface/ISystem";
import { Stack } from "@mui/material";
import { API_URL, handleCopy, handleSendMessage } from "src/utilities";
import { useNotifications } from "@toolpad/core/useNotifications";
import { EditToolbarWebSystem } from "./EditToolbarWebSystem";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "src/context/auth";

export default function WebSystemUser({
  isEdit,
  userRef,
  userWebsystems,
  onClose,
  refetchUser,
}: {
  isEdit: boolean;
  userRef?: string;
  userWebsystems?: IAppUserWebSystem[];
  onClose: () => void;
  refetchUser: () => void;
}) {
  const [rows, setRows] = React.useState<IAppUserWebSystem[]>([]);
  const processRowUpdate = async (newRow: GridRowModel) => {
    const updatedRow: IAppUserWebSystem = { ...newRow, isNew: false }; // علامت‌گذاری ردیف به عنوان "نه جدید"
    await setRows(
      rows.map((row) => {
        // ابتدا سطرهایی که systemRef آنها برابر 9 است
        if (
          newRow.systemRef === 9 &&
          newRow.userName !== "" &&
          newRow.password !== ""
        ) {
          // پیدا کردن سطر با systemRef 8
          let updatedRowForSystem8 = rows.find((r) => r.systemRef === 8);
          if (row.systemRef === 8 && updatedRowForSystem8) {
            // انتقال اطلاعات از سطر با systemRef 9 به سطر با systemRef 8
            updatedRowForSystem8.userName = newRow.userName;
            updatedRowForSystem8.password = newRow.password;

            return updatedRowForSystem8; // سطر با systemRef 8 باید به‌روز شود
          } else {
            return row.id === newRow.id && row.systemRef !== 8
              ? updatedRow
              : row;
          }
        } else if (
          newRow.systemRef === 9 &&
          newRow.userName === "" &&
          newRow.password === ""
        ) {
          let updatedRowForSystem8 = rows.find((r) => r.systemRef === 8);

          if (row.systemRef === 8 && updatedRowForSystem8) {
            updatedRowForSystem8.userName = "";
            updatedRowForSystem8.password = "";

            return updatedRowForSystem8; // سطر با systemRef 8 باید به‌روز شود
          } else {
            return row.id === newRow.id && row.systemRef !== 8
              ? updatedRow
              : row;
          }
        } else {
          return row.id === newRow.id && row.systemRef !== 8 ? updatedRow : row;
        }
      })
    );
    //setRows(rows.map((row) => row.id === newRow.id ? updatedRow : row));

    return newRow.systemRef === 8
      ? rows.find((r) => r.systemRef === 8)
      : updatedRow;
  };
  const notifications = useNotifications();

  const { user } = useAuth();
  const { data: SystemsData } = useQuery<ISystem[]>({
    queryKey: ["SystemsData"],
    queryFn: () =>
      fetch(API_URL + "systems", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user?.token}`,
          Aceept: "application/json",
        },
      }).then((res) => res.json()),
  });

  React.useEffect(() => {
    if (SystemsData) {
      const sortedSystems = SystemsData.sort(
        (a: any, b: any) => a.priorityShow - b.priorityShow
      );

      if (isEdit && userWebsystems && userWebsystems.length > 0) {
        setRows(
          sortedSystems.map((item: ISystem) => ({
            id: item.id,
            MainId: userWebsystems?.find((r: any) => r.systemRef === item.id)
              ?.id,
            customerRef: "",
            systemRef: item.id,
            userName:
              userWebsystems?.find((r: any) => r.systemRef === item.id)
                ?.userName || "",
            password:
              userWebsystems?.find((r: any) => r.systemRef === item.id)
                ?.password || "",
            trackingCode:
              userWebsystems?.find((r: any) => r.systemRef === item.id)
                ?.trackingCode || "",
            system: item,
            isNew: userWebsystems?.find((r: any) => r.systemRef === item.id)
              ? true
              : false,
            selected: userWebsystems?.find((r: any) => r.systemRef === item.id)
              ? true
              : false,
          }))
        );
      } else
        setRows(
          sortedSystems.map((item: ISystem) => ({
            id: item.id,
            MainId: rows?.find((r) => r.id === item.id)?.MainId,
            customerRef: "",
            systemRef: item.id,
            userName: rows?.find((r) => r.id === item.id)?.userName || "",
            password: rows?.find((r) => r.id === item.id)?.password || "",
            trackingCode:
              rows?.find((r) => r.id === item.id)?.trackingCode || "",
            system: item,
            isNew: rows?.find((r) => r.id === item.id)?.selected ? true : false,
            selected: rows?.find((r) => r.id === item.id)?.selected || false,
          }))
        );
    }
  }, [isEdit, userRef, SystemsData]);

  const columns: GridColDef[] = [
    {
      field: "actions",
      type: "actions",
      headerName: "عملیات",
      minWidth: 150,
      cellClassName: "actions",
      getActions: ({ id, row }) => {
        return [
          <GridActionsCellItem
            icon={<InsertLinkIcon />}
            label="OpenLink"
            sx={{
              color: "primary.main",
            }}
            onClick={() => window.open(row.system?.url, "_blank")}
          />,
        ];
      },
    },
    {
      field: "systemRef",
      headerName: "سامانه",
      minWidth: 150,
      filterable: true,

      flex: 1,

      sortable: true,
      renderCell: (params: GridRenderCellParams<any, string>) =>
        `${params.row.system?.name}`,
    },

    {
      field: "userName",
      headerName: "نام کاربری",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
      renderCell: (params: GridRenderCellParams<any, string>) => (
        <Stack
          direction={"row"}
          alignItems={"center"}
          // justifyContent={"space-between"}
        >
          {params.value}
          {params.value != "" && (
            <GridActionsCellItem
              icon={<ContentCopyIcon />}
              label="کپی"
              className="textPrimary"
              onClick={() => handleCopy(params.value, notifications)}
              color="inherit"
            />
          )}
        </Stack>
      ),
    },

    {
      field: "password",
      headerName: "پسورد",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
      renderCell: (params: GridRenderCellParams<any, string>) => (
        <Stack
          direction={"row"}
          alignItems={"center"}
          //justifyContent={"space-between"}
        >
          {params.value}
          {params.value != "" && (
            <GridActionsCellItem
              icon={<ContentCopyIcon />}
              label="کپی"
              className="textPrimary"
              onClick={() => handleCopy(params.value, notifications)}
              color="inherit"
            />
          )}
        </Stack>
      ),
    },

    {
      field: "trackingCode",
      headerName: "کد رهگیری",
      minWidth: 100,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
      renderCell: (params: GridRenderCellParams<any, string>) => (
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          {params.value}
          {params.value != "" && (
            <GridActionsCellItem
              icon={<ContentCopyIcon />}
              label="کپی"
              className="textPrimary"
              onClick={() => handleCopy(params.value, notifications)}
              color="inherit"
            />
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <DataGrid
        //@ts-ignore
        rows={rows}
        columns={columns}
        slots={{
          toolbar: () => (
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              spacing={2}
            >
              {EditToolbarWebSystem && (
                <EditToolbarWebSystem
                  onClose={onClose}
                  existingSystems={userWebsystems}
                  userRef={userRef}
                  updatingSystems={rows}
                  setUpdatingSystems={setRows}
                  refetchUser={refetchUser}
                />
              )}
            </Stack>
          ),
        }}
        editMode="row"
        processRowUpdate={processRowUpdate}
      />
    </Box>
  );
}
